import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import ByMonth from './ByMonth';
import ByCustomPeriod from './ByCustomPeriod';
import { COLORS } from 'styles/variables';
import { useHistory, useLocation } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { RootState } from 'app/store';
import { useSelector } from 'react-redux';
import {
  getMerchantSubscriptionPlans,
  getMerchantV2,
  registerMerchantSubscriptionPlan,
  registerMerchantSubscriptionPlanWithId,
  registerSubscriptionPlan
} from 'services/merchantService';
import { ModalAccountReportSubscription } from 'components/ModalAccountReportSubscription';
import { handleError } from 'utils';
import Loader from 'components/Loader';
import {
  FILTER_SUCCESS_SUBSCRIPTION_LIST,
  MERCHANT_PLAN_CALLBACK_PATHNAME,
  PAYMENT_TOKEN_KEY
} from 'const/Const';
import RetrySubscribePlanModal from 'components/RetrySubscribePlanModal/RetrySubscribePlanModal';
import { MerchantSubscriptionPlan } from 'types/Merchant';

const FinancialReportStyle = styled.div`
  margin: 4rem 0;

  .report-option {
    position: relative;
    display: flex;
    align-items: center;
    background-color: ${COLORS.WHITE};
    border: 1px solid ${COLORS.GREY_4};
    color: ${COLORS.BLACK_2};
    letter-spacing: 0px;
    min-width: 23rem;
    height: 4rem;
    margin-right: 1.4rem;
    border-radius: 2rem;

    svg {
      position: absolute;
      top: 50%;
      right: 1.7rem;
      transform: translateY(-50%);
    }
  }
`;

const FinancialReport = () => {
  const [mode, setMode] = useState<'month' | 'period'>('month');
  const [isChangingSite, setIsChangingSite] = useState(false);
  const history = useHistory();
  const location = useLocation<{ prevPath?: string }>();
  const { merchantSlug } = useSelector((state: RootState) => state.user);
  const queryClient = useQueryClient();
  const { data } = useQuery(
    'getMerchant',
    ({ signal }) => getMerchantV2(merchantSlug, signal),
    { staleTime: Infinity, enabled: true }
  );
  const { data: dataSubscriptionPlans, isLoading: isSubscriptionPlansLoading } =
    useQuery('getMerchantSubscriptionPlans', ({ signal }) =>
      getMerchantSubscriptionPlans(FILTER_SUCCESS_SUBSCRIPTION_LIST, signal)
    );
  const subscriptionPlans = dataSubscriptionPlans?.['hydra:member'] || [];

  const {
    mutateAsync: mutateRegisterSubscriptionPlan,
    isLoading: isRegisterSubscriptionPlanLoading
  } = useMutation(
    ({ slug, data }: { slug: string; data: MerchantSubscriptionPlan }) =>
      registerSubscriptionPlan(slug, data),
    {}
  );

  const { isLoading, mutate, isError } = useMutation({
    mutationKey: ['registerSubscription'],
    mutationFn: registerMerchantSubscriptionPlanWithId,
    onSettled: () => {
      history.replace('/payment/financial-report');
      localStorage.removeItem(PAYMENT_TOKEN_KEY);
      queryClient.invalidateQueries(['getMerchantSubscriptionPlans']);
    }
  });

  const { isLoading: isRetryingRegister, mutateAsync } = useMutation({
    mutationFn: registerMerchantSubscriptionPlan,
    onError: () => handleError(),
    onSettled: () => setIsChangingSite(false)
  });

  useEffect(() => {
    if (location.pathname === MERCHANT_PLAN_CALLBACK_PATHNAME) {
      const paymentToken = localStorage.getItem(PAYMENT_TOKEN_KEY);

      if (paymentToken) {
        mutate();
      } else {
        history.replace('/payment/financial-report');
      }
    }
  }, [
    location.pathname,
    mutate,
    history,
    data?.slug,
    mutateRegisterSubscriptionPlan
  ]);

  const setDisplayMode = (_mode: typeof mode) => {
    setMode(_mode);
  };

  const handleOnClickCancel = () => {
    const prevPath = location.state?.prevPath || '/home';

    history.replace(prevPath);
  };

  const isShowModal = data
    ? !data.isAccountingReport && subscriptionPlans.length === 0
    : false;

  return (
    <>
      {(isLoading ||
        isChangingSite ||
        isRetryingRegister ||
        isSubscriptionPlansLoading ||
        isRegisterSubscriptionPlanLoading) && <Loader />}
      <RetrySubscribePlanModal
        retryFn={() => {
          mutateAsync(window.origin).then((data) => {
            if (
              data.data &&
              data.data.redirect_url &&
              data.data.payment_token
            ) {
              localStorage.setItem(PAYMENT_TOKEN_KEY, data.data.payment_token);

              setIsChangingSite(true);
              window.location.href = data.data.redirect_url;
            }
          });
        }}
        defaultShow={isError}
        key={`error-dialog-${isError}`}
      />
      {isShowModal ? (
        <ModalAccountReportSubscription onClose={handleOnClickCancel} />
      ) : (
        <FinancialReportStyle>
          {mode === 'month' ? (
            <ByMonth
              setDisplayMode={setDisplayMode}
              subscriptionPlans={subscriptionPlans}
              key={
                subscriptionPlans.map((item) => item.id).join('') +
                `${data?.isAccountingReport}`
              }
              merchant={data}
            />
          ) : (
            <ByCustomPeriod
              setDisplayMode={setDisplayMode}
              subscriptionPlans={subscriptionPlans}
              merchant={data}
            />
          )}
        </FinancialReportStyle>
      )}
    </>
  );
};

export default FinancialReport;
